/** "Import from Zoom / Google Meet / Phone": pick a recent recording the notetaker has transcribed. */
import { useState } from "react";

import { Button } from "@/components/ui/button";

import {
  NOTETAKER_SOURCES,
  type Notetaker,
  type NotetakerSource,
  type Recording,
} from "./notetaker";

export const NotetakerImport = ({
  notetaker,
  accountName,
  onImport,
}: {
  notetaker: Notetaker;
  accountName: string;
  onImport: (recording: Recording) => void;
}) => {
  const [source, setSource] = useState<NotetakerSource | null>(null);
  const [recordings, setRecordings] = useState<Recording[] | null>(null);
  const [error, setError] = useState("");

  const open = async (next: NotetakerSource) => {
    setSource(next);
    setRecordings(null);
    setError("");
    try {
      setRecordings(await notetaker.listRecordings(next, { accountName }));
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load recordings.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Import from</span>
        {NOTETAKER_SOURCES.map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={option === source ? "secondary" : "outline"}
            onClick={() => open(option)}
          >
            {option}
          </Button>
        ))}
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {source && !recordings && !error ? (
        <p className="text-sm text-muted-foreground">
          Loading {source} recordings…
        </p>
      ) : null}
      {recordings?.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No recent {source} recordings.
        </p>
      ) : null}
      {recordings?.length ? (
        <ul
          className="divide-y rounded-md border text-sm"
          aria-label={`${source} recordings`}
        >
          {recordings.map((recording) => (
            <li
              key={recording.id}
              className="flex items-center justify-between gap-4 p-2"
            >
              <span className="flex flex-col">
                <span>{recording.title}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(recording.started_at).toLocaleString()} ·{" "}
                  {recording.minutes} min
                </span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  onImport(recording);
                  setSource(null);
                  setRecordings(null);
                }}
              >
                Use this
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
